/* Existing consent-aware carousel, with a visual progress indicator. */
if (!customElements.get('az-youtube-carousel')) {
  customElements.define('az-youtube-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.az-videos__track');
      this.items = [...this.querySelectorAll('.az-videos__item')];
      if (!this.track) return;
      this.abort = new AbortController();
      this.loading = false;
      this.loaded = false;
      this.metadata = new Map();
      this.cards = this.items.filter((item) => item.dataset.videoId && item.querySelector('[data-auto-title], [data-auto-thumbnail]'));
      this.controls = this.querySelector('.az-videos__controls');
      this.previous = this.querySelector('[data-direction="-1"]');
      this.next = this.querySelector('[data-direction="1"]');
      this.count = this.querySelector('.az-videos__count');
      this.consent = this.querySelector('.az-videos__consent');
      this.status = this.querySelector('.az-videos__status');
      const options = { signal: this.abort.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('.az-videos__item');
        if (item) this.track.scrollTo({ left: item.offsetLeft - this.items[0].offsetLeft, behavior: 'auto' });
      }, options);
      this.querySelector('[data-load-videos]')?.addEventListener('click', () => {
        this.allowed = true;
        this.loadMetadata();
      }, options);
      document.addEventListener('az:consent-state', (event) => {
        this.allowed = Boolean(event.detail?.preferences);
        if (this.allowed && this.visible) this.loadMetadata();
        if (!this.allowed) this.resetMetadata();
      }, options);
      this.allowed = Boolean(window.azConsentState?.preferences);
      // A moved section can retain its DOM; remove prior remote metadata if consent is absent.
      if (!this.allowed) this.resetMetadata();
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.track);
      this.visibility = new IntersectionObserver((entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting);
        if (this.visible && this.allowed && !this.attempted) this.loadMetadata();
      }, { rootMargin: '200px' });
      this.visibility.observe(this);
      if (this.consent) this.consent.hidden = !this.cards.length;
      this.update();
    }
    move(direction) {
      const distance = this.items[1] ? this.items[1].offsetLeft - this.items[0].offsetLeft : this.track.clientWidth;
      this.track.scrollBy({ left: direction * distance, behavior: this.dataset.hover === 'false' || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    resetMetadata() {
      this.request?.abort();
      this.loading = false;
      this.loaded = false;
      this.attempted = false;
      this.metadata.clear();
      this.cards.forEach((card) => {
        const title = card.querySelector('[data-auto-title]');
        if (title) title.textContent = title.dataset.fallbackTitle;
        const image = card.querySelector('[data-auto-thumbnail]');
        if (image) {
          image.hidden = true;
          image.removeAttribute('src');
        }
      });
      if (this.consent) this.consent.hidden = !this.cards.length;
      this.status.hidden = true;
    }
    async loadMetadata() {
      if (!this.allowed || this.loading || this.loaded || !this.cards.length) return;
      this.loading = true;
      this.attempted = true;
      const request = new AbortController();
      this.request = request;
      this.status.textContent = 'Videoinformationen werden geladen …';
      this.status.hidden = false;
      const signal = AbortSignal.any([this.abort.signal, request.signal]);
      const results = await Promise.allSettled(this.cards.map(async (card) => {
        const id = card.dataset.videoId;
        if (!/^[a-zA-Z0-9_-]{11}$/.test(id)) throw new Error('Invalid video');
        const image = card.querySelector('[data-auto-thumbnail]');
        // The standard thumbnail still works if oEmbed is unavailable.
        if (image) this.setThumbnail(image, `https://i.ytimg.com/vi/${id}/hqdefault.jpg`);
        if (!card.querySelector('[data-auto-title]')) return;
        if (!this.metadata.has(id)) {
          const url = new URL('https://www.youtube.com/oembed');
          url.search = new URLSearchParams({ url: `https://www.youtube.com/watch?v=${id}`, format: 'json' });
          const pending = fetch(url, { credentials: 'omit', signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) })
            .then(async (response) => {
              if (!response.ok) throw new Error('YouTube unavailable');
              const data = await response.json();
              if (typeof data.title !== 'string' || !data.title.trim()) throw new Error('Missing title');
              return data;
            });
          this.metadata.set(id, pending);
          pending.catch(() => {
            if (this.metadata.get(id) === pending) this.metadata.delete(id);
          });
        }
        const data = await this.metadata.get(id);
        if (signal.aborted || !this.allowed) return;
        card.querySelector('[data-auto-title]').textContent = data.title;
        if (image && typeof data.thumbnail_url === 'string') {
          const thumbnail = new URL(data.thumbnail_url);
          if (thumbnail.protocol === 'https:' && (thumbnail.hostname === 'i.ytimg.com' || thumbnail.hostname.endsWith('.ytimg.com'))) this.setThumbnail(image, thumbnail.href);
        }
      }));
      if (signal.aborted || this.request !== request) return;
      this.loading = false;
      const failed = results.some((result) => result.status === 'rejected');
      this.loaded = !failed;
      this.status.hidden = !failed;
      if (failed) this.status.textContent = 'Einige Videoinformationen konnten nicht geladen werden. Die YouTube-Links funktionieren weiterhin.';
      if (this.consent) this.consent.hidden = !failed;
      this.update();
    }
    setThumbnail(image, source) {
      image.onerror = () => { image.hidden = true; };
      image.src = source;
      image.hidden = false;
    }
    update() {
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const step = this.items[1] ? this.items[1].offsetLeft - this.items[0].offsetLeft : this.track.clientWidth;
      const activeIndex = Math.min(this.items.length - 1, Math.max(0, Math.round(this.track.scrollLeft / (step || 1))));
      this.controls.hidden = maxScroll <= 2;
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= maxScroll - 2;
      if (this.items.length) {
        this.count.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(this.items.length).padStart(2, '0')}`;
        this.count.setAttribute('aria-label', `Video ${activeIndex + 1} von ${this.items.length}`);
        const ratio = maxScroll > 0 ? this.track.scrollLeft / maxScroll : 1;
        this.style.setProperty('--az-video-progress', `${Math.max(8, Math.min(100, ratio * 100))}%`);
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.request?.abort();
      this.observer?.disconnect();
      this.visibility?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
